import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SourceAttributionBadge } from '@/components/catalog/source-attribution-badge'
import { CartPreviewDialog } from '@/components/checkout/cart-preview-dialog'
import { OrderAtSourceButton } from '@/components/checkout/order-at-source-button'
import { groupCartItemsBySource } from '@/features/checkout/cart'
import { formatUsd, rollupBom } from '@/features/projects/rollup-bom'
import type { ProjectDetail } from '@/api/types'

interface BomPanelProps {
  project: ProjectDetail
  variant?: 'card' | 'plain'
}

const formatQty = (qty: number): string => {
  if (Number.isInteger(qty)) return String(qty)
  return qty.toFixed(2).replace(/\.?0+$/, '')
}

export const BomPanel = ({ project, variant = 'card' }: BomPanelProps) => {
  const rollup = useMemo(() => rollupBom(project), [project])
  const isEmpty = rollup.materials.length === 0 && rollup.tools.length === 0
  const cartGroups = useMemo(
    () => groupCartItemsBySource([...rollup.materials, ...rollup.tools]),
    [rollup.materials, rollup.tools]
  )
  const [openCartSlug, setOpenCartSlug] = useState<string | null>(null)
  const openGroup = openCartSlug
    ? (cartGroups.find((g) => g.sourceSlug === openCartSlug) ?? null)
    : null

  const body = (
    <div className="space-y-5">
      <section>
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Materials
        </h3>
        {rollup.materials.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">None.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {rollup.materials.map((line) => (
              <li
                key={line.catalogMaterialId}
                className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5"
              >
                <span className="flex flex-1 items-center gap-2 truncate">
                  <span className="truncate">{line.name}</span>
                  {line.priceTier !== 'default' && (
                    <SourceAttributionBadge
                      tier={line.priceTier}
                      liveSource={line.liveSource}
                      isStale={line.isLivePriceStale}
                    />
                  )}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatQty(line.totalQty)} {line.unit} × {formatUsd(line.unitPriceCents)}
                </span>
                <span className="w-20 text-right tabular-nums">
                  {formatUsd(line.lineTotalCents)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tools</h3>
        {rollup.tools.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">None.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {rollup.tools.map((line) => (
              <li key={line.catalogToolId} className="flex items-baseline justify-between gap-3">
                <span className="flex flex-1 items-center gap-2 truncate">
                  <span className="truncate">{line.name}</span>
                  {line.priceTier !== 'default' && (
                    <SourceAttributionBadge
                      tier={line.priceTier}
                      liveSource={line.liveSource}
                      isStale={line.isLivePriceStale}
                    />
                  )}
                </span>
                <span className="w-20 text-right tabular-nums">
                  {formatUsd(line.unitPriceCents)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="h-px w-full bg-border" />

      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium">Grand total</span>
        <span className="text-base font-semibold tabular-nums">
          {isEmpty ? 'Nothing to total yet.' : formatUsd(rollup.grandTotalCents)}
        </span>
      </div>

      {cartGroups.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t pt-3">
          {cartGroups.map((group) => (
            <OrderAtSourceButton
              key={group.sourceSlug}
              sourceDisplayName={group.sourceDisplayName}
              itemCount={group.items.length}
              subtotalCents={group.subtotalCents}
              onClick={() => setOpenCartSlug(group.sourceSlug)}
            />
          ))}
        </div>
      )}
      {openGroup && (
        <CartPreviewDialog
          group={openGroup}
          open={!!openCartSlug}
          onOpenChange={(o) => !o && setOpenCartSlug(null)}
        />
      )}
    </div>
  )

  if (variant === 'plain') {
    return body
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Bill of materials</CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  )
}
